"use client";

import Link from "next/link";
import Image from "next/image";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Clock } from "@/components/icons";
import { Pagination } from "@/components/ui/pagination";
import { ACCENT_COLOR } from "./data";

const DEFAULT_PAGE_SIZE = 6;

const CARD_BG = "color-mix(in srgb, var(--surface-1) 45%, var(--background))";
const CARD_BORDER = "color-mix(in srgb, var(--foreground) 8%, transparent)";

const CATEGORY_ACCENT: Record<string, string> = {
  "IP Source": "#5b8cff",
  Protocol: "#60A5FA",
  Position: "#A78BFA",
  Access: "#FBBF24",
  Rotation: "#22D3EE",
  "IP Tech": "#F472B6",
  Anonymity: "#FB7185",
  Combo: "#F0ABFC",
  Platform: "#22d3ee",
};

const ACCENT_FALLBACK = ACCENT_COLOR;

export type DynamicCardItem = {
  id: string;
  category: string;
  title: string;
  excerpt: string;
  href: string;
  date: string;
  readTime: string;
  imageUrl?: string | null;
};

interface Props {
  /** Supplied by the server: `proxy_article` documents from Prismic, preceded by the
   *  two product-page cards. See `./fetch.ts`. */
  cards: DynamicCardItem[];
  pageSize?: number;
}

export default function DynamicCards({ cards, pageSize = DEFAULT_PAGE_SIZE }: Props) {
  const reduced = useReducedMotion() ?? false;
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const orderedCards = [...cards.filter((c) => c.imageUrl), ...cards.filter((c) => !c.imageUrl)];

  const totalPages = Math.ceil(orderedCards.length / pageSize);
  const rawPage = parseInt(searchParams.get("page") ?? "1", 10);
  const currentPage =
    isNaN(rawPage) || rawPage < 1 ? 1 : rawPage > totalPages ? totalPages : rawPage;
  const start = (currentPage - 1) * pageSize;
  const pageCards = orderedCards.slice(start, start + pageSize);

  function handlePageChange(page: number) {
    router.push(`${pathname}?page=${page}`, { scroll: false });
    document
      .getElementById("proxy-types-heading")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section
      aria-labelledby="proxy-types-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-xl">
          <h2
            id="proxy-types-heading"
            className="text-foreground mb-3 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Types of Proxy Server
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Every proxy type, indexed by what makes it different: IP source, protocol, position,
            access, rotation, and anonymity.
          </p>
        </div>

        <ul className="grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pageCards.map((card, i) => {
            const accent = CATEGORY_ACCENT[card.category] ?? ACCENT_FALLBACK;
            return (
              <motion.li
                key={card.id}
                initial={reduced ? false : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.4, delay: Math.min(i, 5) * 0.06 }}
              >
                <Link
                  href={card.href}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl transition-colors"
                  style={{
                    background: CARD_BG,
                    border: `1px solid ${CARD_BORDER}`,
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
                    }}
                  />
                  <div
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "300 / 157",
                      background: CARD_BG,
                      borderBottom: `1px solid ${CARD_BORDER}`,
                    }}
                  >
                    {card.imageUrl ? (
                      (() => {
                        const isPriority = currentPage === 1 && i === 0;
                        const isEager = currentPage === 1 && i > 0;
                        return (
                          <Image
                            src={card.imageUrl}
                            /* Decorative: the <h3> right below carries the same
                               text, and the Prismic artwork renders the title
                               inside the image too — a real alt would make screen
                               readers announce each card's name twice. */
                            alt=""
                            fill
                            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            className="object-cover transition-transform duration-500"
                            style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
                            priority={isPriority}
                            {...(isPriority ? {} : { loading: isEager ? "eager" : "lazy" })}
                          />
                        );
                      })()
                    ) : (
                      <div
                        className="absolute inset-0 opacity-70"
                        style={{
                          background: `radial-gradient(ellipse at top left, ${accent}26, transparent 70%)`,
                        }}
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  <div className="relative flex flex-1 flex-col p-5">
                    <h3 className="text-foreground mb-2 text-lg leading-snug font-semibold transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 flex-1 text-sm leading-relaxed">
                      {card.excerpt}
                    </p>
                    <div className="text-muted-foreground flex items-center gap-3 text-xs">
                      <span>{card.date}</span>
                      <span className="text-foreground/15" aria-hidden="true">
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} aria-hidden="true" />
                        {card.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.li>
            );
          })}
        </ul>

        <Pagination current={currentPage} total={totalPages} onChange={handlePageChange} />
      </div>
    </section>
  );
}
